import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Plus, X, Search, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { btn } from "@/components/common/btn";

export function AddLineItemButton({ scopeId, scopeName, getPartsForScopeName, addLineItemFromPart, addLineItemsFromParts, addLineItem, scopeIdx, allParts, variant = "button" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 480 });
  const searchRef = useRef(null);
  const btnRef = useRef(null);

  const calcPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const dropW = 480;
    // Align left edge to button, but keep within viewport
    let left = rect.left;
    if (left + dropW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - dropW - 8);
    // Open below button; if not enough room below, open upward
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= 320 ? rect.bottom + 4 : rect.top - 324;
    setDropPos({ top, left, width: dropW });
  }, []);

  useEffect(() => {
    if (open) {
      calcPosition();
      if (searchRef.current) searchRef.current.focus();
    }
  }, [open, calcPosition]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (e.key === "Escape") { setOpen(false); setSearch(""); } };
    window.addEventListener("keydown", close);
    window.addEventListener("scroll", calcPosition, true);
    window.addEventListener("resize", calcPosition);
    return () => {
      window.removeEventListener("keydown", close);
      window.removeEventListener("scroll", calcPosition, true);
      window.removeEventListener("resize", calcPosition);
    };
  }, [open, calcPosition]);

  const scopeParts = useMemo(() => getPartsForScopeName(scopeName), [scopeName, getPartsForScopeName]);

  const filtered = useMemo(() => {
    if (!search.trim()) return { scopeParts, otherParts: [] };
    const q = search.toLowerCase();
    const matchedScope = scopeParts.filter(p => (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.part_number || "").toLowerCase().includes(q));
    const scopePartIds = new Set(scopeParts.map(p => p.id));
    const matchedOther = allParts.filter(p => !scopePartIds.has(p.id) && ((p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.part_number || "").toLowerCase().includes(q)));
    return { scopeParts: matchedScope, otherParts: matchedOther };
  }, [search, scopeParts, allParts]);

  const [picked, setPicked] = useState(() => new Set());
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const closeAll = () => { setOpen(false); setSearch(""); setPicked(new Set()); };
  const handleSelect = (part) => {
    addLineItemFromPart(scopeId, part);
    closeAll();
  };
  const addPicked = () => {
    const byId = new Map(allParts.map((p) => [p.id, p]));
    const parts = [...picked].map((id) => byId.get(id)).filter(Boolean);
    if (addLineItemsFromParts) addLineItemsFromParts(scopeId, parts); else parts.forEach((p) => addLineItemFromPart(scopeId, p));
    closeAll();
  };
  const PartRow = ({ part, tone }) => (
    <div className={`flex w-full items-center gap-2 px-3 py-1.5 hover:bg-blue-50 ${picked.has(part.id) ? "bg-blue-50/60" : ""}`} data-testid={`part-row-${part.id}`}>
      <input type="checkbox" checked={picked.has(part.id)} onChange={() => togglePick(part.id)} className="h-3.5 w-3.5 shrink-0 accent-blue-600" aria-label={`Select ${part.name}`} data-testid={`part-pick-${part.id}`} />
      <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => (picked.size ? togglePick(part.id) : handleSelect(part))} title={picked.size ? "Add to selection" : "Add this part now"}>
        <BookOpen className={`w-3.5 h-3.5 shrink-0 ${tone}`} />
        <div className="text-sm font-medium truncate">{part.name}{part.part_number ? <span className="text-slate-400 font-normal ml-1.5">[{part.part_number}]</span> : ""}</div>
      </button>
    </div>
  );

  return (
    <div className="relative">
      {variant === "link" ? (
        <button ref={btnRef} type="button" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline dark:text-emerald-400" onClick={() => setOpen(!open)} data-testid={`btn-add-item-${scopeIdx}`}>
          <Plus className="w-3 h-3" /> Item
        </button>
      ) : (
        <Button ref={btnRef} type="button" variant="outline" size="sm" className="h-7 text-xs w-full border-dashed" onClick={() => setOpen(!open)} data-testid={`btn-add-item-${scopeIdx}`}>
          <Plus className="w-3 h-3 mr-1" /> Add Line Item
        </Button>
      )}
      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div className="fixed inset-0 z-[9998]" onClick={closeAll} />
          <div
            className="fixed z-[9999] bg-white border rounded-lg shadow-2xl flex flex-col"
            style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 320 }}
            data-testid={`add-item-popover-${scopeIdx}`}
          >
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 bg-slate-50 rounded-md px-2">
                <Search className="w-3.5 h-3.5 text-slate-400" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search equipment library..."
                  className="h-8 flex-1 bg-transparent text-sm outline-none"
                  data-testid={`search-library-${scopeIdx}`}
                />
                {search && <button onClick={() => setSearch("")} className="text-slate-300 hover:text-slate-500"><X className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.scopeParts.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[10px] uppercase text-slate-400 font-semibold tracking-wide bg-slate-50">This Scope</div>
                  {filtered.scopeParts.map(part => <PartRow key={part.id} part={part} tone="text-blue-500" />)}
                </>
              )}
              {filtered.otherParts.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[10px] uppercase text-slate-400 font-semibold tracking-wide bg-slate-50 border-t">Other Scopes</div>
                  {filtered.otherParts.map(part => <PartRow key={part.id} part={part} tone="text-slate-400" />)}
                </>
              )}
              {filtered.scopeParts.length === 0 && filtered.otherParts.length === 0 && search && (
                <div className="px-3 py-4 text-center text-sm text-slate-400">No items found</div>
              )}
            </div>
            <div className="border-t flex items-center">
              <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left" onClick={() => { addLineItem(scopeId); closeAll(); }} data-testid={`btn-add-custom-item-${scopeIdx}`}>
                <Plus className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="text-sm font-medium">Add New Item</span>
              </button>
              {picked.size > 0 && (
                <button type="button" className={`${btn("write")} ml-auto mr-2`} onClick={addPicked} data-testid={`btn-add-picked-${scopeIdx}`}>
                  Add {picked.size} Selected
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

